/**
 * ============================================================================
 * DISPUTE TIMELINE COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Visual timeline of dispute events
 */

import React from 'react';
import { useI18n } from '../../providers';
import StatusBadge from '../common/StatusBadge';
import Avatar from '../common/Avatar';

/**
 * Dispute Timeline Component
 * @param {Object} props
 * @param {Object} props.dispute - Dispute object
 * @param {Array} props.events - Timeline events
 * @returns {JSX.Element}
 */
const DisputeTimeline = ({ dispute, events = [] }) => {
  const { formatDate, formatTime } = useI18n();

  // Build timeline from dispute data and events
  const timelineEvents = [
    {
      id: 'created',
      type: 'dispute_created',
      title: 'Dispute Created',
      description: dispute.description || 'Issue raised',
      actor: dispute.buyerName || 'Buyer',
      timestamp: dispute.openedAt || dispute.createdAt,
      icon: '🚨',
      color: 'red',
    },
    ...(dispute.sellerResponse
      ? [
          {
            id: 'response',
            type: 'seller_response',
            title: 'Seller Responded',
            description: dispute.sellerResponse,
            actor: dispute.sellerName || 'Seller',
            timestamp: dispute.sellerRespondedAt,
            icon: '💬',
            color: 'blue',
          },
        ]
      : []),
    ...(dispute.offers || []).map((offer, index) => ({
      id: `offer-${offer.id || index}`,
      type: 'offer',
      title: offer.status === 'accepted' ? 'Offer Accepted' : 'Offer Made',
      description: `${offer.offerType === 'full_refund' ? 'Full refund' : `${offer.refundPercentage}% refund`}${offer.message ? `: ${offer.message}` : ''}`,
      actor: offer.senderName || 'User',
      timestamp: offer.createdAt,
      icon: offer.status === 'accepted' ? '✅' : '💰',
      color: offer.status === 'accepted' ? 'green' : 'yellow',
    })),
    ...(dispute.mediationRequestedAt
      ? [
          {
            id: 'mediation',
            type: 'mediation_requested',
            title: 'Mediation Requested',
            description: 'Case escalated to WekaTrust team',
            actor: 'System',
            timestamp: dispute.mediationRequestedAt,
            icon: '⚖️',
            color: 'purple',
          },
        ]
      : []),
    ...(dispute.mediationStatus === 'resolved' && dispute.mediationDecisionDate
      ? [
          {
            id: 'decision',
            type: 'mediation_decision',
            title: 'Decision Made',
            description: dispute.mediationDecision || 'Final decision reached',
            actor: 'WekaTrust Team',
            timestamp: dispute.mediationDecisionDate,
            icon: '⚖️',
            color: 'green',
          },
        ]
      : []),
    ...events,
  ].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  const getColorClasses = (color) => {
    const colors = {
      red: 'bg-red-500 border-red-500 text-red-600 dark:text-red-400',
      blue: 'bg-blue-500 border-blue-500 text-blue-600 dark:text-blue-400',
      green: 'bg-green-500 border-green-500 text-green-600 dark:text-green-400',
      yellow: 'bg-yellow-500 border-yellow-500 text-yellow-600 dark:text-yellow-400',
      purple: 'bg-purple-500 border-purple-500 text-purple-600 dark:text-purple-400',
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-6">
      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <span className="text-xl">📊</span>
        Timeline
      </h3>

      {timelineEvents.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-4">📅</div>
          <p className="text-gray-600 dark:text-gray-400">No events yet</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 via-green-500 to-purple-500" />

          {/* Events */}
          <div className="space-y-6">
            {timelineEvents.map((event, index) => (
              <div
                key={event.id || index}
                className="relative flex items-start gap-4 animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Icon */}
                <div
                  className={`
                    relative z-10 flex-shrink-0
                    w-12 h-12 rounded-full
                    flex items-center justify-center
                    border-4 border-white dark:border-gray-800
                    ${getColorClasses(event.color)}
                    shadow-lg
                  `}
                >
                  <span className="text-xl">{event.icon}</span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {event.title}
                    </h4>
                    <StatusBadge
                      variant={
                        event.color === 'green'
                          ? 'success'
                          : event.color === 'red'
                          ? 'error'
                          : event.color === 'yellow'
                          ? 'warning'
                          : 'info'
                      }
                      size="sm"
                    >
                      {event.type.replace('_', ' ')}
                    </StatusBadge>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {event.description}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Avatar
                      name={event.actor}
                      size="xs"
                      className="flex-shrink-0"
                    />
                    <span>{event.actor}</span>
                    <span>•</span>
                    <span>{formatDate(event.timestamp)}</span>
                    <span>•</span>
                    <span>{formatTime(event.timestamp)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeTimeline;
